import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MapPin, 
  Phone, 
  Clock, 
  CheckCircle2, 
  FileSpreadsheet,
  Zap,
  Flame,
  AlertTriangle,
  Home,
  Award,
  Building2,
  Ruler,
  Droplets,
  FileCheck,
  Star,
  Calendar,
  Euro
} from 'lucide-react';

const DiagnosticValence = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  const villes = [
    "Valence 26000", "Bourg-lès-Valence 26500", "Portes-lès-Valence 26800",
    "Beaumont-lès-Valence 26760", "Montéléger 26760", "Chabeuil 26120",
    "Montmeyran 26120", "Malissard 26120", "Upie 26120", "Étoile-sur-Rhône 26800",
    "Beauvallon 26800", "Montoison 26800", "Livron-sur-Drôme 26250",
    "Loriol-sur-Drôme 26270", "Crest 26400", "Grane 26400", "Allex 26400", "Eurre 26400"
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="pt-32 pb-16 px-4 bg-gradient-to-br from-white to-gray-100"
    >
      <div className="container mx-auto max-w-4xl">
        {/* Hero Section */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <motion.div variants={itemVariants} className="flex justify-center mb-6">
            <div className="bg-gradient-to-r from-dk-blue to-dk-light-blue p-4 rounded-full">
              <MapPin className="h-12 w-12 text-white" />
            </div>
          </motion.div>
          <motion.h1 variants={itemVariants} className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
            Diagnostic Immobilier Valence et Drôme (26)
          </motion.h1>
          <motion.p variants={itemVariants} className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Expert certifié en diagnostics immobiliers à Valence, Bourg-lès-Valence, Crest et dans toute la Drôme. 
            DPE, amiante, électricité, gaz, termites - Intervention rapide dans le 26.
          </motion.p>
        </motion.div>

        {/* Article Content */}
        <motion.article
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="prose prose-lg max-w-none"
        >
          {/* Introduction */}
          <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-3xl font-bold mb-6 text-gray-900 flex items-center">
              <Award className="h-8 w-8 text-dk-blue mr-4" />
              Diagnostics Immobiliers dans la Drôme : Votre Expert Local
            </h2>
            
            <p className="text-gray-700 leading-relaxed mb-6 text-lg">
              La <strong>Drôme (26)</strong> est un département dynamique où le marché immobilier nécessite des diagnostics 
              précis et conformes aux dernières réglementations. De <strong>Valence</strong>, préfecture du département, 
              aux charmantes communes comme <strong>Crest</strong>, <strong>Livron-sur-Drôme</strong> ou <strong>Montélimar</strong>, 
              chaque transaction immobilière doit respecter les obligations légales en matière de diagnostic.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6 text-lg">
              <strong>DIAGNOSTI-K</strong>, votre diagnostiqueur immobilier certifié, intervient dans toute la Drôme 
              pour accompagner propriétaires, acquéreurs, bailleurs et locataires dans leurs projets immobiliers. 
              Basé à Camaret-sur-Aigues, nous nous déplaçons rapidement sur <strong>Valence 26000</strong>, 
              <strong>Bourg-lès-Valence 26500</strong>, <strong>Portes-lès-Valence 26800</strong> et dans l'ensemble 
              des communes drômoises.
            </p>

            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <CheckCircle2 className="h-6 w-6 text-dk-blue mr-3" />
                Pourquoi choisir DIAGNOSTI-K dans la Drôme ?
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Star className="h-5 w-5 text-dk-blue mr-3 mt-1" />
                  <span><strong>Diagnostiqueur certifié</strong> avec toutes les qualifications requises</span>
                </li>
                <li className="flex items-start">
                  <Clock className="h-5 w-5 text-dk-blue mr-3 mt-1" />
                  <span><strong>Intervention rapide</strong> dans toute la Drôme sous 48h</span>
                </li>
                <li className="flex items-start">
                  <FileCheck className="h-5 w-5 text-dk-blue mr-3 mt-1" />
                  <span><strong>Rapports détaillés</strong> remis sous 72h maximum</span>
                </li>
                <li className="flex items-start">
                  <Euro className="h-5 w-5 text-dk-blue mr-3 mt-1" />
                  <span><strong>Tarifs transparents</strong> et devis gratuit</span>
                </li>
              </ul>
            </div>
          </motion.div>

          {/* Services détaillés */}
          <motion.div variants={itemVariants} className="bg-gradient-to-r from-dk-blue to-dk-light-blue text-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-3xl font-bold mb-8 flex items-center">
              <FileSpreadsheet className="h-8 w-8 mr-4" />
              Nos Services de Diagnostic dans la Drôme
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white/10 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <FileSpreadsheet className="h-6 w-6 mr-3" />
                  <h3 className="text-xl font-semibold">DPE Valence et Drôme</h3>
                </div>
                <p className="text-white/90 mb-4">
                  Le <strong>Diagnostic de Performance Énergétique</strong> est obligatoire pour toute vente ou location 
                  dans la Drôme. Nous réalisons vos DPE à Valence, Crest, Montélimar et dans toutes les communes du 26.
                </p>
                <ul className="text-sm text-white/80 space-y-1">
                  <li>• Évaluation énergétique complète</li>
                  <li>• Classement de A à G</li>
                  <li>• Recommandations d'amélioration</li>
                  <li>• Validité 10 ans</li>
                </ul>
              </div>

              <div className="bg-white/10 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <Zap className="h-6 w-6 mr-3" />
                  <h3 className="text-xl font-semibold">Diagnostic Électricité Drôme</h3>
                </div>
                <p className="text-white/90 mb-4">
                  Vérification complète des installations électriques dans les logements de plus de 15 ans 
                  à Valence, Bourg-lès-Valence, Portes-lès-Valence et dans toute la Drôme.
                </p>
                <ul className="text-sm text-white/80 space-y-1">
                  <li>• Contrôle de conformité</li>
                  <li>• Détection des anomalies</li>
                  <li>• Sécurité des occupants</li>
                  <li>• Validité 3 ans (vente) / 6 ans (location)</li>
                </ul>
              </div>

              <div className="bg-white/10 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <Flame className="h-6 w-6 mr-3" />
                  <h3 className="text-xl font-semibold">Diagnostic Gaz Drôme</h3>
                </div>
                <p className="text-white/90 mb-4">
                  Contrôle sécuritaire des installations gaz dans les logements drômois équipés d'installations 
                  de plus de 15 ans. Intervention à Chabeuil, Montmeyran, Malissard et environs.
                </p>
                <ul className="text-sm text-white/80 space-y-1">
                  <li>• Vérification des canalisations</li>
                  <li>• Test d'étanchéité</li>
                  <li>• Contrôle des appareils</li>
                  <li>• Prévention des risques</li>
                </ul>
              </div>

              <div className="bg-white/10 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <AlertTriangle className="h-6 w-6 mr-3" />
                  <h3 className="text-xl font-semibold">Diagnostic Amiante Drôme</h3>
                </div>
                <p className="text-white/90 mb-4">
                  Recherche d'amiante obligatoire dans les bâtiments construits avant 1997 dans la Drôme. 
                  Expertise à Livron-sur-Drôme, Loriol-sur-Drôme, Étoile-sur-Rhône et alentours.
                </p>
                <ul className="text-sm text-white/80 space-y-1">
                  <li>• Repérage des matériaux amiantés</li>
                  <li>• Évaluation de l'état de conservation</li>
                  <li>• Recommandations de gestion</li>
                  <li>• Protection de la santé</li>
                </ul>
              </div>
            </div>

            <div className="bg-white/10 rounded-lg p-6">
              <h3 className="text-xl font-semibold mb-4">Autres diagnostics disponibles dans la Drôme :</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex items-center">
                  <Home className="h-5 w-5 mr-2" />
                  <span>Termites</span>
                </div>
                <div className="flex items-center">
                  <Droplets className="h-5 w-5 mr-2" />
                  <span>Plomb</span>
                </div>
                <div className="flex items-center">
                  <Ruler className="h-5 w-5 mr-2" />
                  <span>Carrez</span>
                </div>
                <div className="flex items-center">
                  <FileCheck className="h-5 w-5 mr-2" />
                  <span>ERP</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Zone d'intervention */}
          <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-3xl font-bold mb-6 text-gray-900 flex items-center">
              <MapPin className="h-8 w-8 text-dk-blue mr-4" />
              Zone d'Intervention dans la Drôme
            </h2>
            
            <p className="text-gray-700 leading-relaxed mb-6 text-lg">
              Notre diagnostiqueur certifié intervient dans l'ensemble du département de la <strong>Drôme (26)</strong> 
              et ses principales communes. Que vous soyez à Valence, dans la vallée du Rhône, ou dans les communes 
              rurales, nous nous déplaçons rapidement pour réaliser vos diagnostics immobiliers.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Building2 className="h-6 w-6 text-dk-blue mr-3" />
                  Agglomération Valentinoise
                </h3>
                <div className="grid grid-cols-1 gap-2 text-sm">
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-dk-blue mr-2" />
                    <span><strong>Valence 26000</strong> - Préfecture de la Drôme</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-dk-blue mr-2" />
                    <span><strong>Bourg-lès-Valence 26500</strong></span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-dk-blue mr-2" />
                    <span><strong>Portes-lès-Valence 26800</strong></span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-dk-blue mr-2" />
                    <span><strong>Beaumont-lès-Valence 26760</strong></span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-dk-blue mr-2" />
                    <span><strong>Montéléger 26760</strong></span>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Home className="h-6 w-6 text-dk-blue mr-3" />
                  Communes Périphériques
                </h3>
                <div className="grid grid-cols-1 gap-2 text-sm">
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-dk-blue mr-2" />
                    <span><strong>Chabeuil 26120</strong></span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-dk-blue mr-2" />
                    <span><strong>Montmeyran 26120</strong></span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-dk-blue mr-2" />
                    <span><strong>Malissard 26120</strong></span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-dk-blue mr-2" />
                    <span><strong>Upie 26120</strong></span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-dk-blue mr-2" />
                    <span><strong>Étoile-sur-Rhône 26800</strong></span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-gray-100 to-gray-200 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Toutes nos communes d'intervention dans la Drôme :</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                {villes.map((ville, index) => (
                  <div key={index} className="flex items-center">
                    <MapPin className="h-4 w-4 text-dk-blue mr-2 flex-shrink-0" />
                    <span className="font-medium">{ville}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Spécificités locales */}
          <motion.div variants={itemVariants} className="bg-gradient-to-r from-dk-blue to-dk-light-blue text-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <Award className="h-8 w-8 mr-4" />
              Spécificités du Marché Immobilier Drômois
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white/10 rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-4">Patrimoine Ancien</h3>
                <p className="text-white/90 mb-4">
                  La Drôme compte de nombreux bâtiments anciens, notamment à <strong>Valence</strong>, <strong>Crest</strong> 
                  et dans les villages historiques. Ces biens nécessitent une attention particulière pour les diagnostics 
                  amiante et plomb.
                </p>
                <ul className="text-sm text-white/80 space-y-1">
                  <li>• Diagnostic amiante obligatoire (avant 1997)</li>
                  <li>• Diagnostic plomb (avant 1949)</li>
                  <li>• Vérification des installations anciennes</li>
                </ul>
              </div>

              <div className="bg-white/10 rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-4">Développement Urbain</h3>
                <p className="text-white/90 mb-4">
                  L'agglomération valentinoise connaît un fort développement avec de nouveaux quartiers et rénovations. 
                  Les diagnostics énergétiques sont essentiels pour ces projets.
                </p>
                <ul className="text-sm text-white/80 space-y-1">
                  <li>• DPE pour les constructions récentes</li>
                  <li>• Audits énergétiques pour rénovations</li>
                  <li>• Diagnostics avant travaux</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Processus et délais */}
          <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-3xl font-bold mb-6 text-gray-900 flex items-center">
              <Clock className="h-8 w-8 text-dk-blue mr-4" />
              Notre Processus dans la Drôme
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="text-center">
                <div className="bg-gradient-to-r from-dk-blue to-dk-light-blue text-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold">1</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Contact</h3>
                <p className="text-gray-600 text-sm">
                  Appelez-nous ou demandez un devis en ligne. Réponse rapide pour toute la Drôme.
                </p>
              </div>

              <div className="text-center">
                <div className="bg-gradient-to-r from-dk-blue to-dk-light-blue text-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold">2</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Intervention</h3>
                <p className="text-gray-600 text-sm">
                  Déplacement sous 48h à Valence, Crest ou dans votre commune drômoise.
                </p>
              </div>

              <div className="text-center">
                <div className="bg-gradient-to-r from-dk-blue to-dk-light-blue text-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold">3</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Rapport</h3>
                <p className="text-gray-600 text-sm">
                  Remise des diagnostics sous 72h maximum par email et courrier.
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Calendar className="h-6 w-6 text-dk-blue mr-3" />
                Horaires d'Intervention dans la Drôme
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="font-medium text-gray-900">Lundi - Vendredi</p>
                  <p className="text-gray-700">8h00 - 19h00</p>
                  <p className="text-sm text-gray-600">Interventions sur rendez-vous</p>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Samedi</p>
                  <p className="text-gray-700">Sur rendez-vous</p>
                  <p className="text-sm text-gray-600">Selon disponibilités</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Call to Action */}
          <motion.div variants={itemVariants} className="bg-gradient-to-r from-dk-blue to-dk-light-blue text-white rounded-xl shadow-lg p-8 text-center">
            <h2 className="text-3xl font-bold mb-6">Besoin d'un Diagnostic dans la Drôme ?</h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto">
              Contactez DIAGNOSTI-K pour tous vos diagnostics immobiliers à Valence, Crest, Montélimar 
              et dans toute la Drôme. Intervention rapide et tarifs transparents.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a 
                href="tel:0789307500"
                className="btn bg-white text-dk-blue hover:bg-gray-100 text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="h-5 w-5 mr-2" />
                07 89 30 75 00
              </a>
              <Link
                to="/contact"
                className="btn bg-white/10 hover:bg-white/20 text-white border-2 border-white text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                Demander un devis
              </Link>
            </div>
          </motion.div>
        </motion.article>
      </div>
    </motion.div>
  );
};

export default DiagnosticValence;